import { useCallback, useMemo } from "react";

import { isEqual } from "lodash";
import {
  createEnumParam,
  decodeJson,
  encodeJson,
  type QueryParamConfig,
  withDefault,
} from "serialize-query-params";
import { StringParam, useQueryParams } from "use-query-params";

import { useUser } from "@components/user-context";
import { logEvent } from "@internal/analytics";
import {
  type Breakdown,
  type FixedDateInterval,
  todayLocalAsISODate,
  ColumnSet,
  type DateRangeChoiceV2,
  Granularity,
  type IColumnSet,
  type ICompareDateRangeEnum,
  type IGranularity,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
  type ITimeGranularity,
  MCAttributionWindowDays,
  NBAccountingMode,
  resolveCompareDateRange,
  TimeGranularity_Deprecated,
} from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

const AttributionWindowParam = createEnumParam<IMCAttributionWindowDays>(
  MCAttributionWindowDays.keyOrder,
);
const AccountingModeParam = createEnumParam<INBAccountingMode>(
  NBAccountingMode.keyOrder,
);
const ColumnSetParam = createEnumParam<IColumnSet>(ColumnSet.keyOrder);
const GranularityParam = createEnumParam<IGranularity>(Granularity.keyOrder);
const TimeGranularityParam = createEnumParam<ITimeGranularity>(
  TimeGranularity_Deprecated.keyOrder,
);

const CustomJsonParam = <T>(): QueryParamConfig<T> => ({
  encode: (value) => encodeJson(value),
  decode: (value) => decodeJson(value),
  equals: (a, b) => isEqual(a, b),
});
type DateRangeType<T> = T | FixedDateInterval;
const DateRangeParam = CustomJsonParam<DateRangeType<DateRangeChoiceV2>>();
const CompareDateRangeParam =
  CustomJsonParam<DateRangeType<ICompareDateRangeEnum>>();
const BreakdownParam = CustomJsonParam<Breakdown[]>();

enum ReportBody {
  ATTRIBUTION_MODEL = "attributionModel",
  ATTRIBUTION_WINDOW = "attributionWindow",
  NB_ACCOUNTING_MODE = "nbAccountingMode",
  COLUMN_SET = "columnSet",
  GRANULARITY = "granularity",
  BREAKDOWNS = "breakdowns",
  TIME_GRANULARITY = "timeGranularity",
  DATE_RANGE = "dateRange",
  COMPARE_DATE_RANGE = "compareDateRange",
  SEARCH_VALUE = "searchValue",
}

export interface ReportBodyState {
  readonly attributionModel: string;
  readonly attributionWindow: IMCAttributionWindowDays;
  readonly nbAccountingMode: INBAccountingMode;
  readonly columnSet: IColumnSet;
  readonly granularity: IGranularity;
  readonly timeGranularity: ITimeGranularity;
  readonly dateRange: DateRangeType<DateRangeChoiceV2>;
  readonly compareDateRange: DateRangeType<ICompareDateRangeEnum>;
  readonly breakdowns: Breakdown[];
}

export type NewReportBodyState = Omit<ReportBodyState, "columnSet">;

export const useReportBodyState = () => {
  const { user } = useUser();
  const [state, setState] = useQueryParams({
    [ReportBody.SEARCH_VALUE]: useMemo(
      () => withDefault(StringParam, ""),
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [user],
    ),
    [ReportBody.ATTRIBUTION_MODEL]: useMemo(
      () => withDefault(StringParam, user.getDefaultAttributionModel("")),
      [user],
    ),
    [ReportBody.ATTRIBUTION_WINDOW]: useMemo(
      () => withDefault(AttributionWindowParam, "1" as const),
      [],
    ),
    [ReportBody.NB_ACCOUNTING_MODE]: useMemo(
      () => withDefault(AccountingModeParam, "accrual" as const),
      [],
    ),
    [ReportBody.COLUMN_SET]: useMemo(
      () => withDefault(ColumnSetParam, "overview" as const),
      [],
    ),
    [ReportBody.GRANULARITY]: useMemo(
      () => withDefault(GranularityParam, "platform" as const),
      [],
    ),
    [ReportBody.TIME_GRANULARITY]: useMemo(
      () => withDefault(TimeGranularityParam, "daily" as const),
      [],
    ),
    [ReportBody.DATE_RANGE]: useMemo(
      () => withDefault(DateRangeParam, "last7Days" as const),
      [],
    ),
    [ReportBody.COMPARE_DATE_RANGE]: useMemo(
      () => withDefault(CompareDateRangeParam, "lastPeriod" as const),
      [],
    ),
    [ReportBody.BREAKDOWNS]: useMemo(() => withDefault(BreakdownParam, []), []),
  });

  const dateRange = useMemo(
    () =>
      parseDateRangeArgument(
        state[ReportBody.DATE_RANGE],
        todayLocalAsISODate(),
      ),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [state[ReportBody.DATE_RANGE]],
  );

  return {
    state,
    setState,
    searchValue: state[ReportBody.SEARCH_VALUE],
    setSearchValue: useCallback(
      (value: string) => {
        setState({ [ReportBody.SEARCH_VALUE]: value });
      },
      [setState],
    ),

    attributionModel: state[ReportBody.ATTRIBUTION_MODEL],
    setAttributionModel: useCallback(
      (value: string) => {
        logEvent("Saved Views: User changed Attribution Model", {
          from: state[ReportBody.ATTRIBUTION_MODEL],
          to: value,
        });
        setState({ [ReportBody.ATTRIBUTION_MODEL]: value });
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [setState],
    ),

    attributionWindow: state[ReportBody.ATTRIBUTION_WINDOW],
    setAttributionWindow: useCallback(
      (value: IMCAttributionWindowDays) => {
        logEvent("Saved Views: User changed Attribution Window", {
          from: state[ReportBody.ATTRIBUTION_WINDOW],
          to: value,
        });
        setState({ [ReportBody.ATTRIBUTION_WINDOW]: value });
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [setState],
    ),

    nbAccountingMode: state[ReportBody.NB_ACCOUNTING_MODE],
    setNbAccountingMode: useCallback(
      (value: INBAccountingMode) => {
        logEvent("Saved Views: User changed Accounting Mode", {
          from: state[ReportBody.NB_ACCOUNTING_MODE],
          to: value,
        });
        setState({ [ReportBody.NB_ACCOUNTING_MODE]: value });
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [setState],
    ),

    columnSet: state[ReportBody.COLUMN_SET],
    setColumnSet: useCallback(
      (value: IColumnSet) => {
        logEvent("Saved Views: User changed Column Set", {
          from: state[ReportBody.COLUMN_SET],
          to: value,
        });
        setState({ [ReportBody.COLUMN_SET]: value });
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [setState],
    ),

    granularity: state[ReportBody.GRANULARITY],
    setGranularity: useCallback(
      (value: IGranularity) => setState({ [ReportBody.GRANULARITY]: value }),
      [setState],
    ),

    breakdowns: state[ReportBody.BREAKDOWNS],
    setBreakdowns: useCallback(
      (value: Breakdown[]) => {
        logEvent("Saved Views: User changed Breakdowns", state);
        setState({ [ReportBody.BREAKDOWNS]: value });
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [setState],
    ),

    timeGranularity: state[ReportBody.TIME_GRANULARITY],
    setTimeGranularity: useCallback(
      (value: ITimeGranularity) => {
        logEvent("Saved Views: User changed Time Granularity", {
          from: state[ReportBody.TIME_GRANULARITY],
          to: value,
        });
        setState({ [ReportBody.TIME_GRANULARITY]: value });
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [setState],
    ),

    dateRange,
    unparsedDateRange: state[ReportBody.DATE_RANGE],
    setDateRange: useCallback(
      (value: FixedDateInterval | DateRangeChoiceV2) => {
        logEvent("Saved Views: User changed Date Range", {
          from: state[ReportBody.DATE_RANGE],
          to: dateRange,
        });
        setState({ [ReportBody.DATE_RANGE]: value });
      },
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
      [setState],
    ),

    compareDateRange: useMemo(
      () => resolveCompareDateRange(dateRange, state.compareDateRange),
      [dateRange, state.compareDateRange],
    ),
    setCompareDateRange: useCallback(
      (value: FixedDateInterval | ICompareDateRangeEnum) =>
        setState({ [ReportBody.COMPARE_DATE_RANGE]: value }),
      [setState],
    ),
  };
};
